import { InfoOutlined } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChangeEvent, FormEvent, useRef, useState } from "react";
import { GET, POST } from "../../../Utilities/BaseService";
import { currencyFormatter } from "../../Reading/Bill/Cells/Last12Summary";

/* ================= API ================= */

const addPaymentCollection = (payload: {
    collections: {
        payment: number;
        amount: number;
        adjustment: number;
    }[];
}) => POST("/payment/collection/bulk", payload);

/* ================= TYPES ================= */

type Props = { updateMode?: boolean };

type Collection = {
    id: number;
    targetType: "reading" | "customer" | "property" | "meter";
    paymentHead: string;
    amount: number;
    remainingAmount: number;
    collection: number;
    adjustment: number;
};

type Collections = Record<number, Collection>;

const initialClearAll = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    reading: false,
    property: false,
    customer: false,
    meter: false,
};

/* ================= COMPONENT ================= */

export const AddPaymentCollection = ({ updateMode = false }: Props) => {
    const [feedback, setFeedback] = useState<any>();
    const [filter, setFilter] = useState<any>({
        district: "",
        village: "",
        hamlet: "",
        powerstation: "",
        targetType: "",
        targetId: "",
    });
    const [pagination, setPagination] = useState({
        page: 0,
        limit: 10,
        count: 0,
    });
    const [collections, setCollections] = useState<Collections>({});
    const [clearAll, setClearAll] = useState(initialClearAll);

    const collectionRef = useRef<HTMLInputElement | null>(null);

    /* ================= TARGET TYPES ================= */

    const { data: targetTypes } = useQuery(["target-types"], () =>
        GET("/payment/head/targetType")
    );

    /* ================= PAYMENTS ================= */

    const { isLoading } = useQuery(
        [
            "payments",
            filter.targetType,
            filter.targetId,
            pagination.page,
            pagination.limit,
            feedback,
        ],
        () =>
            GET("/payment", {
                targetType: filter.targetType,
                targetId:
                    filter.targetId && filter.targetId !== "all"
                        ? filter.targetId
                        : undefined,
                page: pagination.page + 1,
                limit: pagination.limit,
                completed: 0,
            }),
        {
            onSuccess(res) {
                const mapped = res.data.rows.reduce((prev: any, curr: any) => {
                    const total =
                        curr.amount > 0 ? curr.amount : curr.head?.amount;

                    const paid = curr.collections.reduce(
                        (p: number, c: any) => p + c.amount + c.adjustment,
                        0
                    );

                    return {
                        ...prev,
                        [curr.id]: {
                            id: curr.id,
                            targetType: curr.targetType,
                            paymentHead: curr.head?.name,
                            amount: total,
                            remainingAmount: total - paid,
                            collection: 0,
                            adjustment: 0,
                        },
                    };
                }, {});

                setCollections(mapped);
                setPagination((p) => ({
                    ...p,
                    count: res.data.count,
                }));
            },
        }
    );

    /* ================= MUTATION ================= */

    const addMutation = useMutation(addPaymentCollection, {
        onSuccess(res) {
            setFeedback(res.data);
        },
    });

    /* ================= HANDLERS ================= */

    const handleCollectionChange = (
        e: ChangeEvent<HTMLInputElement>,
        id: number
    ) => {
        const value = Math.max(0, Number(e.target.value));
        const item = collections[id];

        const maxAllowed = item.remainingAmount - item.adjustment;

        setCollections({
            ...collections,
            [id]: {
                ...item,
                collection: Math.min(value, maxAllowed),
            },
        });
    };

    const handleAdjustmentChange = (
        e: ChangeEvent<HTMLInputElement>,
        id: number
    ) => {
        const value = Math.max(0, Number(e.target.value));
        const item = collections[id];

        const maxAllowed = item.remainingAmount - item.collection;

        setCollections({
            ...collections,
            [id]: {
                ...item,
                adjustment: Math.min(value, maxAllowed),
            },
        });
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();

        addMutation.mutate({
            collections: Object.values(collections)
                .filter((c) => c.collection > 0 || c.adjustment > 0)
                .map((c) => ({
                    payment: c.id,
                    amount: c.collection,
                    adjustment: c.adjustment,
                })),
        });
    };

    /* ================= RENDER ================= */

    return (
        <Box>
            <Typography variant="h5">Payment Collection</Typography>

            {!Object.values(collections).length && !isLoading && (
                <Alert sx={{ mt: 2 }} severity="info">
                    No payments found
                </Alert>
            )}

            {!!Object.values(collections).length && (
                <>
                    <TablePagination
                        component="div"
                        count={pagination.count}
                        page={pagination.page}
                        rowsPerPage={pagination.limit}
                        onPageChange={(_, p) =>
                            setPagination({
                                ...pagination,
                                page: p,
                            })
                        }
                        onRowsPerPageChange={(e) =>
                            setPagination({
                                ...pagination,
                                limit: Number(e.target.value),
                                page: 0,
                            })
                        }
                    />

                    <TableContainer component="form" onSubmit={handleSubmit}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Target</TableCell>
                                    <TableCell>Payment Head</TableCell>
                                    <TableCell>Total</TableCell>
                                    <TableCell>Remaining</TableCell>
                                    <TableCell>Collection</TableCell>
                                    <TableCell>Adjustment</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {Object.entries(collections).map(
                                    ([key, value]) => (
                                        <TableRow key={key}>
                                            <TableCell>
                                                <IconButton disabled>
                                                    <InfoOutlined />
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{value.id}</TableCell>
                                            <TableCell>
                                                {value.targetType}
                                            </TableCell>
                                            <TableCell>
                                                {value.paymentHead}
                                            </TableCell>
                                            <TableCell>
                                                {currencyFormatter.format(
                                                    value.amount
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {value.remainingAmount}
                                            </TableCell>

                                            <TableCell>
                                                <TextField
                                                    ref={collectionRef}
                                                    size="small"
                                                    variant="outlined"
                                                    type="number"
                                                    value={value.collection}
                                                    onChange={(e: any) =>
                                                        handleCollectionChange(
                                                            e,
                                                            Number(key)
                                                        )
                                                    }
                                                    inputProps={{
                                                        min: 0,
                                                        max:
                                                            value.remainingAmount -
                                                            value.adjustment,
                                                    }}
                                                    sx={{
                                                        width: 120,
                                                    }}
                                                />
                                            </TableCell>

                                            {/* ADJUSTMENT */}
                                            <TableCell>
                                                <TextField
                                                    variant="outlined"
                                                    type="number"
                                                    size="small"
                                                    value={value.adjustment}
                                                    onChange={(e: any) =>
                                                        handleAdjustmentChange(
                                                            e,
                                                            Number(key)
                                                        )
                                                    }
                                                    inputProps={{
                                                        min: 0,
                                                        max:
                                                            value.remainingAmount -
                                                            value.collection,
                                                    }}
                                                    sx={{
                                                        width: 120,
                                                    }}
                                                />
                                            </TableCell>

                                            {/* COLLECTION */}
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>

                        <Button
                            fullWidth
                            sx={{ mt: 2 }}
                            variant="outlined"
                            type="submit"
                            disabled={
                                addMutation.isLoading ||
                                !Object.values(collections).some(
                                    (c) => c.collection > 0 || c.adjustment > 0
                                )
                            }
                            endIcon={
                                addMutation.isLoading ? (
                                    <CircularProgress size={20} />
                                ) : undefined
                            }
                        >
                            {addMutation.isLoading
                                ? "Collecting..."
                                : "Collect"}
                        </Button>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};
